import { useState } from 'react';
import { Button } from '../../components/ui/button';
import { FileSpreadsheet, Image as ImageIcon, Copy, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';
import * as XLSX from 'xlsx';
import { parseLocalISO } from '../../lib/dateUtils';

// Inline WhatsApp logo — lucide-react'te yok; küçük tek-renk SVG ile
// "salt simge" buton için ideal (kullanıcı isteği: küçük, sadece ikon).
const WhatsAppIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M.057 24l1.687-6.163a11.867 11.867 0 0 1-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 0 1 8.413 3.488 11.824 11.824 0 0 1 3.48 8.414c-.003 6.554-5.338 11.89-11.893 11.89a11.9 11.9 0 0 1-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 0 0 1.593 5.391L4.4 19.802l3.254-1.609zm5.943-7.6c.211.302.211.728.123 1.029-.085.302-.464.943-.787 1.262-.323.32-.685.32-1.07.245-.385-.075-1.595-.594-3.026-1.871-1.121-.998-1.879-2.232-2.099-2.61-.22-.378-.023-.583.105-.769.115-.166.256-.434.385-.652.128-.218.171-.374.256-.624.086-.25.043-.467-.022-.652-.064-.187-.385-.928-.526-1.27-.139-.336-.281-.291-.385-.291-.099-.005-.213-.005-.327-.005-.115 0-.299.043-.456.218-.157.176-.598.585-.598 1.427 0 .842.612 1.656.697 1.769.085.114 1.205 1.84 2.92 2.581 1.715.74 2.012.616 2.376.577.364-.039 1.173-.479 1.339-.943.166-.464.166-.862.116-.943-.05-.082-.187-.13-.391-.236z"/>
  </svg>
);

/**
 * Filtered board exporter — sits next to the "Aktif filtreler" bar on the
 * Planning page. Produces three high-fidelity outputs of the **currently
 * filtered** weekly plan:
 *
 *   1. Excel (.xlsx)  — explicit "Müşteri | Firma | Depo | Rota | <gün>..."
 *                       columns. No DOM scraping; rows are built from the
 *                       same `routeGroups` / `assignByCell` indices that
 *                       the live board renders, so what the operator sees
 *                       on screen is exactly what lands in the sheet.
 *   2. PNG indir      — captures an OFF-SCREEN clean replica (1280px wide,
 *                       no scroll, no sticky tricks) via html2canvas. The
 *                       live DOM is intentionally NOT used because its
 *                       overflow / sticky / transform contexts scramble
 *                       under html2canvas (operator's "birleşik" complaint).
 *   3. Kopyala        — same PNG written to the clipboard for direct paste
 *                       into WhatsApp / e-mail.
 *
 * Inputs come straight from the parent <Planning/> page so filter state is
 * already applied — no double-filtering needed here.
 */

// Stable tone palette for firm header bars. Keyed by firm-name hash so the
// same firm gets the same color across exports. Mirrors the canvas palette
// in PlanningBoardCanvas so the visual story stays consistent.
const FIRM_TONES = [
  { bg: '#f97316', fg: '#ffffff' }, // orange
  { bg: '#1d4ed8', fg: '#ffffff' }, // blue
  { bg: '#047857', fg: '#ffffff' }, // emerald
  { bg: '#dc2626', fg: '#ffffff' }, // red
  { bg: '#7c3aed', fg: '#ffffff' }, // violet
  { bg: '#0f172a', fg: '#ffffff' }, // slate
  { bg: '#db2777', fg: '#ffffff' }, // pink
];
function firmTone(name) {
  if (!name) return FIRM_TONES[5];
  let h = 0;
  for (const c of name) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return FIRM_TONES[h % FIRM_TONES.length];
}

const TR_WEEKDAYS = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];
const TR_MONTHS = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];

/** "16 Nis Pzt" — locale-stable header label for date columns. */
function dayHeaderLabel(iso) {
  const d = parseLocalISO(iso);
  if (!d) return iso;
  // ISO weekdays: Mon=1..Sun=0  → align to TR_WEEKDAYS[0]=Pzt
  const wd = d.getDay() === 0 ? 6 : d.getDay() - 1;
  return `${d.getDate()} ${TR_MONTHS[d.getMonth()]} ${TR_WEEKDAYS[wd]}`;
}

/** Inline HTML embed güvenliği — kullanıcı isimleri özel karakter içerirse
 *  (örn. & ' ") off-screen DOM'unda HTML olarak yorumlanmasın. */
function escapeHtml(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Resolve the firm name for a (route, assignment) pair. Routes carry an
 *  `owner_firm_name` (contract holder); assignments may override via
 *  `firm_name` when a borrowed worker is logged. We prefer the owner so the
 *  "Firma" column tells the customer who is contractually responsible. */
function resolveFirmName(route, assignment) {
  return route?.owner_firm_name || assignment?.owner_firm_name || assignment?.firm_name || '—';
}

/** Build a flat row model from the same data the live board uses. Each row
 *  represents ONE route across all visible days; cells contain the assigned
 *  employee + plate (or empty).
 *
 *  Taşeron destek (May 03, 2026): atamada `is_subcontractor === true` ise
 *  hücre kind'ı 'subcontractor' olarak işaretleniyor. Renderer ve Excel
 *  generator'lar bu kind'ı amber stille / "TAŞERON" etiketiyle çıkartıyor. */
function buildRows({ routeGroups, assignByCell, dates }) {
  const rows = [];
  for (const c of routeGroups || []) {
    for (const d of c.depots || []) {
      for (const r of d.routes || []) {
        let firstAssignment = null;
        let hasSubcontractor = false;
        const cells = dates.map((iso) => {
          const a = assignByCell.get(`${r.id}|${iso}`);
          if (!a) return null;
          if (!firstAssignment) firstAssignment = a;
          if (a.status === 'depot') {
            return { kind: 'depot', text: 'DEPO' };
          }
          if (a.is_subcontractor) hasSubcontractor = true;
          return {
            kind: a.is_subcontractor ? 'subcontractor' : 'assigned',
            name: a.employee_name || '—',
            plate: (a.vehicle_code || '').trim(),
          };
        });
        rows.push({
          customer: c.company_name || '—',
          firm: resolveFirmName(r, firstAssignment),
          depot: d.depot_name || '—',
          routeCode: r.route_code || r.route_name || '—',
          firmHex: firmTone(r.owner_firm_name || firstAssignment?.owner_firm_name).bg,
          cells,
          hasSubcontractor,
        });
      }
    }
  }
  return rows;
}

export function FilteredBoardExport({
  year, week, board,
  routeGroups, assignByCell, visibleDates,
}) {
  const [busy, setBusy] = useState('');

  const filename = (ext) => `Plan_${year}_W${String(week).padStart(2, '0')}_filtre.${ext}`;

  const dates = (visibleDates && visibleDates.length ? visibleDates : (board?.dates || [])).filter(Boolean);

  // -----------------------------------------------------------------
  //  PNG — Route-major matrix (Şub 02, 2026 v5)
  //  ---------------------------------------------------------------
  //  Operatörün yeni isteği: ekrandaki Planning board görünümünün AYNISI.
  //    • Sol kolon: TUR (route_code + firma rozeti + bölge + saat)
  //    • Üst başlık: TARİHLER (1 Haz Pzt, 2 Haz Sal, ...)
  //    • Hücreler: atanan kişi (ad + plaka), DEPO, taşeron rozeti, vs.
  //  Müşteri → Depo gruplama hiyerarşik başlıklarla; her depo bloku
  //  sticky-like başlıkla görsel olarak ayrılır.
  // -----------------------------------------------------------------

  // Tarih başlığı için Hollandaca/Türkçe karışık değil, sadece TR.
  const formatShortDate = (iso) => dayHeaderLabel(iso);

  const buildOffscreenNode = () => {
    // Visible dates (cmt dahil, pazar atlanıyor — board hafta-içi odaklı)
    const matrixDates = dates.filter((iso) => {
      const d = parseLocalISO(iso);
      return d && d.getDay() !== 0;
    });

    // Layout dimensions — sol "rota" kolonu geniş; tarih hücreleri dar.
    const PADDING = 24;
    const ROUTE_COL_W = 280;
    const CELL_W = 170;
    const TABLE_W = ROUTE_COL_W + Math.max(matrixDates.length, 1) * CELL_W;
    const TOTAL_W = TABLE_W + PADDING * 2;

    const node = document.createElement('div');
    node.style.cssText = `
      position: fixed; left: -10000px; top: 0; z-index: -1;
      width: ${TOTAL_W}px; padding: ${PADDING}px;
      background: #ffffff; color: #1a1a1a;
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif;
      box-sizing: border-box;
    `;

    // Visible groups: routeGroups (customer → depots → routes)
    // FİLTRE: Sadece görünen tarih aralığında EN AZ BİR atama olan route'ları
    // PNG'ye dahil ediyoruz — boş turlar müşteriye gönderilen görselde gürültü
    // yaratıyordu (operatör isteği).
    const hasAnyAssign = (route) => matrixDates.some((iso) => {
      const a = assignByCell.get(`${route.id}|${iso}`);
      return a && a.status !== 'empty'; // 'depot' ve atanan kişi dahil
    });

    const customers = (routeGroups || [])
      .map((c) => ({
        ...c,
        depots: (c.depots || [])
          .map((d) => ({ ...d, routes: (d.routes || []).filter(hasAnyAssign) }))
          .filter((d) => d.routes.length > 0),
      }))
      .filter((c) => (c.depots || []).length > 0);

    if (customers.length === 0) {
      const empty = document.createElement('div');
      empty.style.cssText = 'padding:60px; text-align:center; color:#64748b; font-size:14px; border:1px dashed #cbd5e1; border-radius:8px;';
      empty.innerHTML = `
        <div style="font-size:32px; margin-bottom:8px; opacity:0.4;">∅</div>
        <div style="font-weight:600;">Aktif filtre ile eşleşen rota bulunamadı.</div>
      `;
      node.appendChild(empty);
      document.body.appendChild(node);
      return node;
    }

    // -------- Özet bandı --------
    const summary = (() => {
      const empSet = new Set();
      const subcSet = new Set();
      const firmSet = new Set();
      const plateSet = new Set();
      let routeCount = 0;
      let assignCount = 0;
      for (const c of customers) {
        for (const d of c.depots || []) {
          for (const r of d.routes || []) {
            routeCount++;
            if (r.owner_firm_name) firmSet.add(r.owner_firm_name);
            for (const date of matrixDates) {
              const a = assignByCell.get(`${r.id}|${date}`);
              if (!a || a.status === 'empty') continue;
              if (a.status === 'depot') continue;
              assignCount++;
              const key = a.is_subcontractor ? `subc-${a.subcontractor_id || a.employee_name}` : a.employee_id;
              if (key) empSet.add(key);
              if (a.is_subcontractor && key) subcSet.add(key);
              if (a.vehicle_code) plateSet.add(a.vehicle_code);
            }
          }
        }
      }
      return { routes: routeCount, drivers: empSet.size, subcontractors: subcSet.size,
               firms: firmSet.size, plates: plateSet.size, assignments: assignCount };
    })();

    const summaryBar = document.createElement('div');
    summaryBar.style.cssText = `
      margin-bottom:16px; padding:12px 18px;
      background:linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
      border-radius:10px; display:flex; align-items:center; gap:18px; color:#ffffff;
      box-shadow:0 4px 14px rgba(15,23,42,0.15);
    `;
    const stat = (label, value, accent) => `
      <div style="display:flex; flex-direction:column; align-items:flex-start;">
        <div style="font-size:9px; letter-spacing:1.5px; color:${accent}; font-weight:700; text-transform:uppercase;">${label}</div>
        <div style="font-size:20px; font-weight:800; color:#ffffff; line-height:1.1;">${value}</div>
      </div>
    `;
    const divider = `<div style="width:1px; height:30px; background:#334155;"></div>`;
    summaryBar.innerHTML = `
      <div style="display:flex; flex-direction:column; gap:2px;">
        <div style="font-size:9px; letter-spacing:2.5px; color:#6ee7b7; font-weight:700; text-transform:uppercase;">Bu Hafta · Özet</div>
        <div style="font-size:18px; font-weight:800; color:#ffffff;">W${week} · ${year}</div>
      </div>
      <div style="margin-left:auto; display:flex; align-items:center; gap:16px; flex-wrap:wrap;">
        ${stat('Rota', summary.routes, '#a5b4fc')}
        ${divider}
        ${stat('Atama', summary.assignments, '#6ee7b7')}
        ${divider}
        ${stat('Sürücü', summary.drivers, '#93c5fd')}
        ${divider}
        ${stat('Firma', summary.firms, '#fcd34d')}
        ${summary.subcontractors > 0 ? divider + stat('Taşeron', summary.subcontractors, '#fbbf24') : ''}
        ${summary.plates > 0 ? divider + stat('Araç', summary.plates, '#c4b5fd') : ''}
      </div>
    `;
    node.appendChild(summaryBar);

    // -------- Tek büyük tablo (modern card) --------
    const wrap = document.createElement('div');
    wrap.style.cssText = 'border:1px solid #e2e8f0; border-radius:12px; overflow:hidden; background:#ffffff; box-shadow:0 1px 3px rgba(15,23,42,0.05);';

    const table = document.createElement('table');
    table.style.cssText = 'width:100%; border-collapse:collapse; table-layout:fixed; font-size:12px;';

    // colgroup
    const colgroup = document.createElement('colgroup');
    const cRoute = document.createElement('col');
    cRoute.style.width = `${ROUTE_COL_W}px`;
    colgroup.appendChild(cRoute);
    matrixDates.forEach(() => {
      const c = document.createElement('col');
      c.style.width = `${CELL_W}px`;
      colgroup.appendChild(c);
    });
    table.appendChild(colgroup);

    // header — koyu gradient
    const thead = document.createElement('thead');
    const hr = document.createElement('tr');
    hr.style.cssText = 'background:#1e293b;';
    const routeTh = document.createElement('th');
    routeTh.style.cssText = 'padding:14px 16px; text-align:left; font-size:10px; font-weight:800; color:#94a3b8; border-bottom:2px solid #0f172a; letter-spacing:1.5px; text-transform:uppercase;';
    routeTh.textContent = 'Tur';
    hr.appendChild(routeTh);
    matrixDates.forEach((iso) => {
      const d = parseLocalISO(iso);
      const isSat = d && d.getDay() === 6;
      const th = document.createElement('th');
      const bg = isSat ? '#0f172a' : '#1e293b';
      th.style.cssText = `padding:14px 8px; text-align:center; font-size:12px; font-weight:800; color:#f1f5f9; border-bottom:2px solid #0f172a; border-left:1px solid #334155; background:${bg}; letter-spacing:0.3px; line-height:1.2;`;
      th.innerHTML = `<div style="font-weight:800;">${escapeHtml(formatShortDate(iso))}</div>`;
      hr.appendChild(th);
    });
    thead.appendChild(hr);
    table.appendChild(thead);

    const tbody = document.createElement('tbody');

    // Render hierarchically: customer → depot → routes
    customers.forEach((c) => {
      const depots = (c.depots || []).filter((d) => (d.routes || []).length > 0);
      if (!depots.length) return;

      // Customer header row — modern: turuncu sol border, küçük caps başlık
      const cusTr = document.createElement('tr');
      cusTr.style.cssText = 'background:#f8fafc;';
      const cusTd = document.createElement('td');
      cusTd.colSpan = matrixDates.length + 1;
      cusTd.style.cssText = 'padding:10px 14px; font-size:11px; font-weight:800; color:#0f172a; border:1px solid #e2e8f0; letter-spacing:1.5px; text-transform:uppercase; border-left:4px solid #f97316;';
      cusTd.textContent = c.company_name || '—';
      cusTr.appendChild(cusTd);
      tbody.appendChild(cusTr);

      depots.forEach((d) => {
        // Depot header — inline ve ince
        const depTr = document.createElement('tr');
        depTr.style.cssText = 'background:#ffffff;';
        const depTd = document.createElement('td');
        depTd.colSpan = matrixDates.length + 1;
        depTd.style.cssText = 'padding:6px 22px; font-size:10px; font-weight:700; color:#94a3b8; border:1px solid #e2e8f0; letter-spacing:0.8px; text-transform:uppercase;';
        depTd.textContent = d.depot_name || '—';
        depTr.appendChild(depTd);
        tbody.appendChild(depTr);

        // Each route → one row
        (d.routes || []).forEach((r) => {
          const tone = firmTone(r.owner_firm_name);
          const tr = document.createElement('tr');
          tr.style.cssText = 'background:#ffffff;';

          // Left "route" cell — MODERN layout:
          //   1. Üstte: Firma badge (chip)
          //   2. Ortada: Route code (büyük, bold) + saat (sağda monospace)
          //   3. Altta: Region (ince gri, küçük)
          const tdRoute = document.createElement('td');
          tdRoute.style.cssText = 'padding:12px 14px; border:1px solid #e2e8f0; vertical-align:middle; background:#ffffff;';
          const startTime = r.default_start_time || r.start_time || '';
          const region = r.region_name || '';
          const firmBadgeRoute = r.owner_firm_name
            ? `<table style="border-collapse:collapse; display:inline-table; margin:0 0 6px 0;">
                 <tr>
                   <td style="background:${tone.bg}; color:${tone.fg}; padding:1px 10px 5px 10px; border-radius:10px; font-size:10px; font-weight:800; letter-spacing:0.6px; line-height:1; text-align:center; font-family:'Inter',Arial,sans-serif; text-transform:uppercase; vertical-align:middle;">${escapeHtml(r.owner_firm_name)}</td>
                 </tr>
               </table>`
            : '';
          tdRoute.innerHTML = `
            ${firmBadgeRoute}
            <div style="display:flex; align-items:baseline; gap:10px;">
              <span style="font-family:ui-monospace,SFMono-Regular,Menlo,monospace; font-weight:800; font-size:15px; color:#0f172a; line-height:1;">${escapeHtml(r.route_code || r.route_name || '—')}</span>
              ${startTime ? `<span style="font-family:ui-monospace,SFMono-Regular,Menlo,monospace; font-size:11px; color:#94a3b8; font-weight:600; line-height:1; letter-spacing:0.5px;">${escapeHtml(startTime)}</span>` : ''}
            </div>
            ${region ? `<div style="margin-top:4px; font-size:11px; color:#64748b; font-weight:500; line-height:1.2;">${escapeHtml(region)}</div>` : ''}
          `;
          tr.appendChild(tdRoute);

          // Each date cell
          matrixDates.forEach((iso) => {
            const a = assignByCell.get(`${r.id}|${iso}`);
            const td = document.createElement('td');
            if (!a || a.status === 'empty') {
              td.style.cssText = 'padding:8px 4px; text-align:center; font-size:13px; color:#cbd5e1; background:#f8fafc; border:1px solid #e2e8f0;';
              td.textContent = '—';
            } else if (a.status === 'depot') {
              td.style.cssText = 'padding:8px 4px; text-align:center; font-size:10px; font-weight:800; color:#475569; background:#e2e8f0; border:1px solid #cbd5e1; letter-spacing:0.6px;';
              td.textContent = 'DEPO';
            } else {
              // Atanmış kişi — taşeron / borrowed / normal
              let bg = '#dcfce7';   // emerald-100
              let border = '#86efac';
              let fg = '#14532d';
              let badge = '';
              if (a.is_subcontractor) {
                bg = '#fef3c7'; border = '#fcd34d'; fg = '#78350f';
                badge = `<table style="border-collapse:collapse; display:inline-table; margin:0;">
                  <tr><td style="background:#b45309; color:#fff; padding:0 8px 4px 8px; border-radius:10px; font-size:9.5px; font-weight:800; letter-spacing:0.5px; line-height:1; text-align:center; font-family:'Inter',Arial,sans-serif; text-transform:uppercase; vertical-align:middle;">Taşeron</td></tr>
                </table>`;
              } else if (a.firm_id && r.owner_firm_id && a.firm_id !== r.owner_firm_id) {
                bg = '#fed7aa'; border = '#fb923c'; fg = '#7c2d12';
                badge = `<table style="border-collapse:collapse; display:inline-table; margin:0;">
                  <tr><td style="background:#c2410c; color:#fff; padding:0 8px 4px 8px; border-radius:10px; font-size:9.5px; font-weight:800; letter-spacing:0.5px; line-height:1; text-align:center; font-family:'Inter',Arial,sans-serif; text-transform:uppercase; vertical-align:middle;">${escapeHtml(a.firm_name || 'Ödünç')}</td></tr>
                </table>`;
              }
              td.style.cssText = `padding:10px 10px; text-align:left; vertical-align:middle; background:${bg}; border:1px solid ${border}; color:${fg}; line-height:1.3;`;
              const name = a.employee_name || '—';
              const plate = (a.vehicle_code || '').trim();
              td.innerHTML = `
                ${badge ? `<div style="margin-bottom:4px;">${badge}</div>` : ''}
                <div style="font-weight:800; font-size:12.5px; line-height:1.25;">${escapeHtml(name)}</div>
                ${plate ? `<div style="margin-top:3px; font-family:ui-monospace,SFMono-Regular,Menlo,monospace; font-size:10px; font-weight:700; color:${fg}; opacity:0.85; letter-spacing:0.4px; line-height:1;">${escapeHtml(plate)}</div>` : ''}
              `;
            }
            tr.appendChild(td);
          });

          tbody.appendChild(tr);
        });
      });
    });
    table.appendChild(tbody);
    wrap.appendChild(table);
    node.appendChild(wrap);

    // Footer
    const foot = document.createElement('div');
    foot.style.cssText = 'margin-top:12px; padding:8px 4px; display:flex; align-items:center; justify-content:space-between; font-size:10px; color:#94a3b8;';
    foot.innerHTML = `
      <div style="font-weight:700; color:#475569;">OMS PORTAL · W${week} · ${year}</div>
      <div>${new Date().toLocaleString('tr-TR', { day:'2-digit', month:'long', year:'numeric', hour:'2-digit', minute:'2-digit' })}</div>
    `;
    node.appendChild(foot);

    document.body.appendChild(node);
    return node;
  };

  const capturePng = async () => {
    const node = buildOffscreenNode();
    try {
      // Layout settle for fonts/borders
      await new Promise((r) => setTimeout(r, 80));
      const canvas = await html2canvas(node, {
        scale: 2,
        backgroundColor: '#ffffff',
        useCORS: true,
        logging: false,
        windowWidth: node.scrollWidth,
        windowHeight: node.scrollHeight,
        width: node.scrollWidth,
        height: node.scrollHeight,
      });
      return canvas.toDataURL('image/png');
    } finally {
      try { document.body.removeChild(node); } catch { /* ignore */ }
    }
  };

  const handleDownloadPng = async () => {
    if (busy) return;
    setBusy('png');
    try {
      const dataUrl = await capturePng();
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = filename('png');
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success('PNG indirildi');
    } catch (err) {
      console.error(err);
      toast.error('PNG oluşturulamadı');
    } finally {
      setBusy('');
    }
  };

  const handleCopyPng = async () => {
    if (busy) return;
    setBusy('copy');
    try {
      if (!navigator.clipboard || !window.ClipboardItem) {
        toast.error('Tarayıcınız panoya görsel kopyalamayı desteklemiyor');
        return;
      }
      const dataUrl = await capturePng();
      const blob = await (await fetch(dataUrl)).blob();
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
      toast.success('Panoya kopyalandı — yapıştırabilirsiniz');
    } catch (err) {
      console.error(err);
      toast.error('Panoya kopyalanamadı');
    } finally {
      setBusy('');
    }
  };

  // ─── WhatsApp paylaş ──────────────────────────────────────────────────────
  // Strateji (cihaza göre):
  //   1. Mobil/desteklenen tarayıcı: navigator.share({ files: [pngFile] })
  //      → WhatsApp dahil native paylaş listesi açılır, kullanıcı sohbet seçer.
  //   2. Masaüstü/fallback: PNG'yi panoya kopyala + WhatsApp Web'i yeni sekmede
  //      aç. Operatör sohbete girip Ctrl+V ile yapıştırır (tek tık eksiği var,
  //      ama daha iyi bir yol yok — wa.me URL şeması görsel taşımıyor).
  const handleWhatsApp = async () => {
    if (busy) return;
    setBusy('whatsapp');
    try {
      const dataUrl = await capturePng();
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], filename('png'), { type: 'image/png' });
      const shareData = {
        files: [file],
        title: `OMS Planlama W${week}/${year}`,
        text: `Planlama W${week}/${year}`,
      };
      if (navigator.canShare && navigator.canShare(shareData) && navigator.share) {
        try {
          await navigator.share(shareData);
          return;
        } catch (err) {
          if (err && err.name === 'AbortError') return; // kullanıcı iptal etti
          // başka bir hata → fallback'e düş
        }
      }
      // Fallback: panoya kopyala + WhatsApp Web'i aç.
      if (navigator.clipboard && window.ClipboardItem) {
        await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
        toast.success('PNG panoya kopyalandı — WhatsApp açılıyor, sohbete Ctrl+V ile yapıştırın');
      } else {
        toast.info('Tarayıcı doğrudan paylaşımı desteklemiyor — WhatsApp Web açılıyor');
      }
      window.open('https://web.whatsapp.com/', '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error(err);
      toast.error('WhatsApp paylaşımı başlatılamadı');
    } finally {
      setBusy('');
    }
  };

  // -----------------------------------------------------------------
  //  Excel — explicit Müşteri | Firma | Depo | Rota | <gün>... layout
  // -----------------------------------------------------------------
  const handleExcel = async () => {
    if (busy) return;
    setBusy('xlsx');
    try {
      const rows = buildRows({ routeGroups, assignByCell, dates });
      if (rows.length === 0) {
        toast.warning('Aktif filtreyle eşleşen rota yok — ihraç edilecek veri bulunamadı');
        return;
      }
      const headers = ['Müşteri', 'Firma', 'Depo', 'Rota', ...dates.map(dayHeaderLabel)];
      const aoa = [headers];
      for (const row of rows) {
        const dayCells = row.cells.map((cell) => {
          if (!cell) return '';
          if (cell.kind === 'depot') return 'DEPO';
          // Taşeron'ları Excel'de "TAŞERON: Ad (Plaka)" şeklinde işaretle.
          // Operatör müşteriye gönderdiği sheet'te tek bakışta hangi günü kimin
          // taşeron olarak doldurduğunu görsün.
          const prefix = cell.kind === 'subcontractor' ? '[TAŞERON] ' : '';
          return cell.plate ? `${prefix}${cell.name} (${cell.plate})` : `${prefix}${cell.name}`;
        });
        // "Firma" kolonunu, satırda en az bir taşeron varsa "Taşeron"
        // değeriyle override et — operatör grupları tek kolondan filtreleyebilsin.
        const firmCol = row.hasSubcontractor && row.cells.every((c) => !c || c.kind !== 'assigned')
          ? 'Taşeron'
          : row.firm;
        aoa.push([row.customer, firmCol, row.depot, row.routeCode, ...dayCells]);
      }

      const ws = XLSX.utils.aoa_to_sheet(aoa);

      // Column widths — readable on first open without resizing.
      ws['!cols'] = [
        { wch: 18 },  // Müşteri
        { wch: 16 },  // Firma
        { wch: 18 },  // Depo
        { wch: 14 },  // Rota
        ...dates.map(() => ({ wch: 22 })),
      ];

      // Freeze top row + first 4 columns so scrolling keeps context visible.
      // SheetJS uses `!views` for this; older `!freeze` is ignored.
      ws['!views'] = [{ state: 'frozen', xSplit: 4, ySplit: 1, topLeftCell: 'E2' }];

      // Auto-filter on the header row spans full width.
      const lastCol = XLSX.utils.encode_col(headers.length - 1);
      ws['!autofilter'] = { ref: `A1:${lastCol}1` };

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, `W${week}`);
      XLSX.writeFile(wb, filename('xlsx'));
      toast.success('Excel indirildi');
    } catch (err) {
      console.error(err);
      toast.error('Excel oluşturulamadı');
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="inline-flex items-center gap-1 ml-1" data-testid="filtered-export-group">
      <Button
        size="sm"
        variant="outline"
        className="h-6 px-2 text-[10px] border-emerald-300 text-emerald-700 hover:bg-emerald-50"
        onClick={handleExcel}
        disabled={!!busy}
        data-testid="filtered-export-xlsx"
        title="Excel'e aktar (Müşteri/Firma/Depo/Rota başlıklarıyla)"
      >
        {busy === 'xlsx' ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileSpreadsheet className="h-3 w-3" />}
        <span className="ml-1">Excel</span>
      </Button>
      <Button
        size="sm"
        variant="outline"
        className="h-6 px-2 text-[10px] border-blue-300 text-blue-700 hover:bg-blue-50"
        onClick={handleDownloadPng}
        disabled={!!busy}
        data-testid="filtered-export-png"
        title="PNG olarak indir (yüksek çözünürlük)"
      >
        {busy === 'png' ? <Loader2 className="h-3 w-3 animate-spin" /> : <ImageIcon className="h-3 w-3" />}
        <span className="ml-1">PNG</span>
      </Button>
      <Button
        size="sm"
        variant="outline"
        className="h-6 px-2 text-[10px] border-purple-300 text-purple-700 hover:bg-purple-50"
        onClick={handleCopyPng}
        disabled={!!busy}
        data-testid="filtered-export-copy"
        title="PNG'yi panoya kopyala (WhatsApp/E-posta'ya yapıştır)"
      >
        {busy === 'copy' ? <Loader2 className="h-3 w-3 animate-spin" /> : <Copy className="h-3 w-3" />}
        <span className="ml-1">Kopyala</span>
      </Button>
      {/* WhatsApp paylaş — sadece ikon, küçük; tek tıkla PNG + native share veya
          panoya kopyala + WhatsApp Web (cihaza göre otomatik). */}
      <Button
        size="icon"
        variant="outline"
        className="h-6 w-6 border-[#25D366]/40 text-[#25D366] hover:bg-[#25D366]/10"
        onClick={handleWhatsApp}
        disabled={!!busy}
        data-testid="filtered-export-whatsapp"
        title="WhatsApp ile paylaş (PNG)"
        aria-label="WhatsApp ile paylaş"
      >
        {busy === 'whatsapp' ? <Loader2 className="h-3 w-3 animate-spin" /> : <WhatsAppIcon className="h-3.5 w-3.5" />}
      </Button>
    </div>
  );
}
